import ErrorScreen from '@/shared/components/error/ErrorScreen';
import { LoadingScreen } from '@/shared/components/loader/LoadingScreen';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useAuth } from '../stores/auth';
import { useEffect, useState } from 'react';
import { getMyrclLoginUrl } from '@/app/utils/auth';

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>) => {
    return {
      accessToken: (search.accessToken as string) || '',
      redirect: (search.redirect as string) || '/',
    };
  },
  beforeLoad: ({ context, search }) => {
    if (context.auth.isAuthenticated) {
      throw redirect({ to: search.redirect || '/' });
    }

    if (!search.accessToken) {
      const myrclLoginUrl = getMyrclLoginUrl();
      if (myrclLoginUrl) {
        throw redirect({ href: myrclLoginUrl });
      }
      console.error('VITE_MYRCL_LOGIN_URL is not configured');
    }
  },
  component: AuthenticateUserComponent,
});

function AuthenticateUserComponent() {
  const navigate = useNavigate();
  const {
    loginWithToken,
    isLoading: authLoading,
    error: authError,
    clearError,
  } = useAuth();
  const [isProcessing, setIsProcessing] = useState(true);
  const [localError, setLocalError] = useState<string | null>(null);
  const { accessToken, redirect } = Route.useSearch();

  useEffect(() => {
    const handleTokenLogin = async () => {
      try {
        setIsProcessing(true);
        clearError(); // Clear any previous auth errors
        setLocalError(null);

        if (!accessToken) {
          setLocalError('No access token provided in URL');
          setIsProcessing(false);
          return;
        }

        // Login with the token (validation happens in loginWithToken)
        await loginWithToken(accessToken);

        // Small delay to ensure auth state is fully updated
        setTimeout(() => {
          navigate({ to: redirect });
        }, 300);
      } catch (error) {
        console.error('Authentication failed:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'Authentication failed';
        setLocalError(errorMessage);
        setIsProcessing(false);
      }
    };

    handleTokenLogin();
  }, [accessToken, redirect, loginWithToken, navigate, clearError]);

  // Show error page if there's an error (either local or from auth context)
  const displayError = localError || authError;
  if (displayError) {
    return (
      <ErrorScreen
        error={new Error(displayError)}
        onRetry={() => {
          clearError();
          setLocalError(null);
          window.location.reload();
        }}
      />
    );
  }

  // Show loading screen while processing authentication
  if (isProcessing || authLoading) {
    return <LoadingScreen message="Authenticating..." />;
  }

  // This should not normally be reached
  return <LoadingScreen message="Completing authentication..." />;
}
